import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Database,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCcw,
  Shield,
  Activity,
  Clock,
  Server,
  HardDrive,
  Cpu,
  GitBranch,
  FileText,
  Search,
  Download,
  ChevronRight,
  Info,
  TrendingUp,
  BarChart2
} from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

const DataQualityPage: React.FC = () => {
  const site = useSite();
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Mock data quality metrics
  const qualityMetrics = [
    {
      id: 'completeness',
      name: 'Data Completeness',
      value: 98.5,
      status: 'excellent',
      description: 'Percentage of required fields populated'
    },
    {
      id: 'accuracy',
      name: 'Data Accuracy',
      value: 99.2,
      status: 'excellent',
      description: 'Validated against official sources'
    },
    {
      id: 'timeliness',
      name: 'Data Freshness',
      value: 95.8,
      status: 'good',
      description: 'Updated within SLA timeframes'
    },
    {
      id: 'consistency',
      name: 'Data Consistency',
      value: 97.3,
      status: 'excellent',
      description: 'Cross-source validation passing'
    }
  ];

  const dataSources = [
    {
      id: 'api',
      name: 'Sports API',
      status: 'active',
      lastSync: '2 minutes ago',
      records: '1.2M',
      health: 100,
      icon: Server
    },
    {
      id: 'database',
      name: 'Primary Database',
      status: 'active',
      lastSync: 'Real-time',
      records: '5.8M',
      health: 99,
      icon: Database
    },
    {
      id: 'cache',
      name: 'Redis Cache',
      status: 'active',
      lastSync: '30 seconds ago',
      records: '250K',
      health: 100,
      icon: HardDrive
    },
    {
      id: 'backup',
      name: 'Backup Storage',
      status: 'maintenance',
      lastSync: '1 hour ago',
      records: '5.8M',
      health: 85,
      icon: Shield
    }
  ];

  const validationRules = [
    {
      id: 1,
      rule: 'Team roster validation',
      status: 'passing',
      lastRun: '5 minutes ago',
      issues: 0
    },
    {
      id: 2,
      rule: 'Score consistency check',
      status: 'passing',
      lastRun: '10 minutes ago',
      issues: 0
    },
    {
      id: 3,
      rule: 'Player statistics validation',
      status: 'warning',
      lastRun: '15 minutes ago',
      issues: 3
    },
    {
      id: 4,
      rule: 'Season data integrity',
      status: 'passing',
      lastRun: '20 minutes ago',
      issues: 0
    },
    {
      id: 5,
      rule: 'Championship records validation',
      status: 'passing',
      lastRun: '30 minutes ago',
      issues: 0
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
      case 'active':
      case 'passing':
        return 'text-success';
      case 'good':
      case 'warning':
        return 'text-warning';
      case 'poor':
      case 'error':
      case 'failing':
        return 'text-error';
      case 'maintenance':
        return 'text-secondary';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'excellent':
      case 'active':
      case 'passing':
        return 'badge-success';
      case 'good':
      case 'warning':
        return 'badge-warning';
      case 'poor':
      case 'error':
      case 'failing':
        return 'badge-error';
      case 'maintenance':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  };

  const getHealthColor = (health: number) => {
    if (health >= 95) return 'bg-success';
    if (health >= 80) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Database className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Data Quality Monitor
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">DATA </span>
              <span className="neon-text">QUALITY</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real-time monitoring and validation of {site.sport ? `${site.sport} ` : ''}sports data integrity
            </p>
          </div>

          {/* Overall Health Score */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-8 mb-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center gap-6 mb-4 md:mb-0">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-success/20 to-success/10 flex items-center justify-center">
                    <span className="text-3xl font-bold text-success">98%</span>
                  </div>
                  <CheckCircle className="absolute bottom-0 right-0 w-6 h-6 text-success" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Overall Data Quality</h2>
                  <p className="text-gray-400">System performing optimally</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="px-4 py-2 bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-all flex items-center gap-2">
                  <RefreshCcw className="w-4 h-4" />
                  Run Full Validation
                </button>
                <button className="px-4 py-2 bg-surface/50 text-gray-400 border border-border rounded-lg hover:text-white hover:border-gray-600 transition-all flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>
            </div>
          </motion.div>

          {/* Quality Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {qualityMetrics.map((metric, index) => (
              <motion.div
                key={metric.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="glass-card p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">{metric.name}</h3>
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-white mb-1">
                      {metric.value}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {metric.description}
                    </div>
                  </div>
                  <div className={`badge ${getStatusBadge(metric.status)} text-xs`}>
                    {metric.status}
                  </div>
                </div>
                <div className="mt-4 h-2 bg-surface/50 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${metric.value >= 95 ? 'bg-success' : metric.value >= 80 ? 'bg-warning' : 'bg-error'}`}
                    style={{ width: `${metric.value}%` }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Data Sources */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-primary" />
                Data Sources
              </h2>
              <span className="text-sm text-gray-400">
                Last updated: <span className="text-white">2 minutes ago</span>
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dataSources.map((source) => {
                const Icon = source.icon;
                return (
                  <div 
                    key={source.id}
                    className="p-4 bg-surface/30 rounded-lg border border-border/50 hover:border-primary/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{source.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`badge ${getStatusBadge(source.status)} text-xs`}>
                              {source.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {source.lastSync}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="text-gray-500">Records: </span>
                        <span className="text-white font-semibold">{source.records}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Health</span>
                        <div className="w-20 h-2 bg-surface/50 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getHealthColor(source.health)}`}
                            style={{ width: `${source.health}%` }}
                          />
                        </div>
                        <span className="text-xs text-white font-semibold">{source.health}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Validation Rules */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Validation Rules
              </h2>
              <button className="text-sm text-primary hover:text-primary-light transition-colors">
                View All Rules →
              </button>
            </div>
            
            <div className="space-y-3">
              {validationRules.map((rule) => (
                <div 
                  key={rule.id}
                  className="flex items-center justify-between p-3 bg-surface/30 rounded-lg border border-border/50 hover:border-primary/30 transition-all"
                >
                  <div className="flex items-center gap-3">
                    {rule.status === 'passing' ? (
                      <CheckCircle className="w-5 h-5 text-success" />
                    ) : rule.status === 'warning' ? (
                      <AlertCircle className="w-5 h-5 text-warning" />
                    ) : (
                      <XCircle className="w-5 h-5 text-error" />
                    )}
                    <div>
                      <h4 className="font-medium text-white">{rule.rule}</h4>
                      <p className="text-xs text-gray-500">Last run: {rule.lastRun}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {rule.issues > 0 && (
                      <span className="badge badge-warning text-xs">
                        {rule.issues} issues
                      </span>
                    )}
                    <button className="text-gray-400 hover:text-white transition-colors">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default DataQualityPage;